import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardContent, Typography, ButtonGroup, Button, Box } from '@material-ui/core';
import dayjs from 'dayjs';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

import { GET_PLATFORM_ENTITY_STATS } from '../../graphql/query/statistics';
import fillBuckets from './fillBuckets';

const RANGES = [
  { id: '7d', label: '7d', days: 7, defaultGranularity: 'DAY' },
  { id: '30d', label: '30d', days: 30, defaultGranularity: 'DAY' },
  { id: '90d', label: '90d', days: 90, defaultGranularity: 'WEEK' },
  { id: '365d', label: '365d', days: 365, defaultGranularity: 'MONTH' },
];

const GRANULARITIES = [
  { id: 'DAY', label: 'Day', dataAttr: 'day' },
  { id: 'WEEK', label: 'Week', dataAttr: 'week' },
  { id: 'MONTH', label: 'Month', dataAttr: 'month' },
];

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chartWrapper: {
    width: '100%',
    height: 320,
  },
  title: {
    marginBottom: theme.spacing(1),
  },
}));

const isoWeekFallback = (date) => {
  // Minimal ISO week number without extra plugins.
  const d = new Date(Date.UTC(date.year(), date.month(), date.date()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const safeFormatBucket = (bucket, granularity) => {
  const d = dayjs(bucket);
  if (granularity === 'DAY') return d.format('MMM D');
  if (granularity === 'WEEK') return `W${isoWeekFallback(d)} ${d.format('YYYY')}`;
  if (granularity === 'MONTH') return d.format('MMM YYYY');
  return d.format('YYYY-MM-DD');
};

const StatsChart = ({ title, entity, chartId }) => {
  const classes = useStyles();
  const [rangeId, setRangeId] = useState('30d');
  const range = RANGES.find((r) => r.id === rangeId);
  const [granularity, setGranularity] = useState(range.defaultGranularity);

  // When the range changes, snap granularity to the range's default.
  const onRangeChange = (nextId) => {
    const next = RANGES.find((r) => r.id === nextId);
    setRangeId(nextId);
    setGranularity(next.defaultGranularity);
  };

  const { from, to } = useMemo(() => {
    const today = dayjs().startOf('day');
    return {
      from: today.subtract(range.days, 'day').format('YYYY-MM-DD'),
      to: today.format('YYYY-MM-DD'),
    };
  }, [range.days]);

  const variables = useMemo(
    () => ({ entity, from, to, granularity }),
    [entity, from, to, granularity],
  );

  const { data, loading, error } = useQuery(GET_PLATFORM_ENTITY_STATS, { variables });

  const chartData = useMemo(() => {
    const rows = data?.platformEntityStats || [];
    const filled = fillBuckets(rows, from, to, granularity);
    return filled.map((r) => ({ ...r, label: safeFormatBucket(r.bucket, granularity) }));
  }, [data, from, to, granularity]);

  return (
    <Card className={classes.card} data-test="chart" data-chart={chartId}>
      <CardContent>
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
        <div className={classes.toolbar} data-test="chart-toolbar">
          <ButtonGroup size="small" color="primary">
            {RANGES.map((r) => (
              <Button
                key={r.id}
                data-range={r.id}
                variant={r.id === rangeId ? 'contained' : 'outlined'}
                onClick={() => onRangeChange(r.id)}
              >
                {r.label}
              </Button>
            ))}
          </ButtonGroup>
          <ButtonGroup size="small" color="primary">
            {GRANULARITIES.map((g) => (
              <Button
                key={g.id}
                data-granularity={g.dataAttr}
                variant={g.id === granularity ? 'contained' : 'outlined'}
                onClick={() => setGranularity(g.id)}
              >
                {g.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>
        <Box className={classes.chartWrapper}>
          {error ? (
            <Typography color="error">Failed to load chart</Typography>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1976d2" isAnimationActive={!loading} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

StatsChart.propTypes = {
  title: PropTypes.string.isRequired,
  entity: PropTypes.oneOf(['USERS', 'ITEMS', 'COLLECTIONS']).isRequired,
  chartId: PropTypes.string.isRequired,
};

export default StatsChart;
